import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
  Button,
  Drawer,
  Flex,
  getDefaultZIndex,
  Group,
  Modal,
  Overlay,
  Space,
  Stack,
  Tabs,
  Text,
  Transition,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@constants/queryKeys';
import {
  ViewsTypesEnum,
  MyJobsTabsEnum,
  MY_JOBS_TAB_LABELS,
  TransformedJobItem,
  WorkerJobCommonResultsResponse,
} from '@worker/types/myJobs.type';
import { useRecommendationActions } from '@worker/hooks/useRecommendationActions';
import { useMyJobsQueryKey } from '@worker/hooks/useMyJobsQueryKey';
import { useMyJobsQueryState } from '@worker/hooks/useMyJobsQueryState';
import {
  MyJobsQueryParamsEnum,
  handleMyJobsSearch,
  setOpenJobDetailsInUrl,
  clearOpenJobDetailsFromUrl,
} from '@worker/helpers/my-jobs-helpers';
import { MY_JOBS_PAGE_SIZE } from '@constants/various';
import { useWorkerStore } from '@worker/stores';
import Header from '@worker/components/MyJobs/Header/Header';
import FilterBar from '@worker/components/MyJobs/FilterBar/FilterBar';
import NotFound from '@components/elements/NotFound';
import { BASE_PATH } from '@utils/Environment';
import { AdvancedFiltersFormValues } from '@worker/stores/types';
import AdvancedFilters from '@worker/components/MyJobs/Forms/AdvancedFiltersForm/AdvancedFiltersForm';
import JobsConfigForm from '@worker/components/MyJobs/Forms/JobsConfigForm/JobsConfigForm';
import GetInNetwork from '@worker/components/GetInNetwork/GetInNetwork';
import HeaderMobile from '@worker/components/MyJobs/Header/HeaderMobile';
import Welcome from '@worker/components/MyJobs/Welcome/Welcome';
import JobsList from '@worker/components/MyJobs/JobsList/JobsList';
import Availability from '@worker/components/MyJobs/Availability/Availability';
import JobDetailsModal from '@worker/components/MyJobs/JobDetailsModal/JobDetailsModal';
import {
  JobFarAwayModal,
  ProfileIncompleteModal,
  JobQuestionsModal,
} from '@worker/components/MyJobs/modals';
import { useApplyFlow } from '@worker/hooks/useApplyFlow';

const TABS_CONFIG = [
  { value: MyJobsTabsEnum.NEW, component: JobsList },
  { value: MyJobsTabsEnum.APPLIED, component: JobsList },
  { value: MyJobsTabsEnum.SAVED, component: JobsList },
  { value: MyJobsTabsEnum.AVAILABILITY, component: Availability },
] as const;

function MyJobs() {
  const {
    showAdvancedFilters,
    showConfigureModal,
    showGetInNetworkModal,
    currentView,
    currentTab,
    workerProfile,
    searchFilters,
    setShowAdvancedFilters,
    setShowMobileFilters,
    setShowConfigureModal,
    setShowGetInNetworkModal,
    setCurrentView,
    setCurrentTab,
    setSearchFilters,
    setAdvancedFilters,
    resetSearchFilters,
    resetAllFilters,
  } = useWorkerStore();

  const { jobScope, tabName } = useParams();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const { urlParams } = useMyJobsQueryState();
  const queryClient = useQueryClient();
  const listQueryKey = useMyJobsQueryKey();
  const tabQueryParamsRef = useRef<Record<string, string>>({
    [MyJobsTabsEnum.NEW]: '',
    [MyJobsTabsEnum.APPLIED]: '',
    [MyJobsTabsEnum.SAVED]: '',
    [MyJobsTabsEnum.AVAILABILITY]: '',
  });

  // Show welcome modal when worker hasn't completed setup yet (setupCompletedAt is null)
  const showWelcome = !workerProfile.jobPreferences?.setupCompletedAt;

  const autoSaveDefaultsRef = useRef<(() => void) | null>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [isSavingDefaults, setIsSavingDefaults] = useState(false);

  // Job details modal — open state driven by ?openJobDetails= (parsed via useMyJobsQueryState).
  const showJobDetailsModal = urlParams.openJobDetails != null;

  // Action handlers (dismiss/save/share/message) + shared mutation state for the toolbar.
  const recommendationActions = useRecommendationActions();

  // Application flow hook - handles profile/distance checks and questions modal
  const applyFlow = useApplyFlow();

  const isValidJobScope =
    jobScope &&
    Object.values(ViewsTypesEnum).includes(jobScope as ViewsTypesEnum);
  const isValidTabName =
    tabName &&
    Object.values(MyJobsTabsEnum).includes(tabName as MyJobsTabsEnum);

  useEffect(() => {
    if (isValidJobScope) {
      setCurrentView(jobScope as ViewsTypesEnum);
    }
    if (isValidTabName) {
      setCurrentTab(tabName as MyJobsTabsEnum);
    }
  }, [
    jobScope,
    tabName,
    isValidJobScope,
    isValidTabName,
    setCurrentView,
    setCurrentTab,
  ]);

  // Handle opening In-Network benefits modal after successful payment
  useEffect(() => {
    if (searchParams.has(MyJobsQueryParamsEnum.OPEN_IN_NETWORK_BENEFITS)) {
      setShowGetInNetworkModal(true);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete(MyJobsQueryParamsEnum.OPEN_IN_NETWORK_BENEFITS);
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, setShowGetInNetworkModal]);

  // Handle opening Configure modal via link (e.g. from notification settings page)
  useEffect(() => {
    if (searchParams.has(MyJobsQueryParamsEnum.OPEN_CONFIGURE)) {
      setShowConfigureModal(true);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete(MyJobsQueryParamsEnum.OPEN_CONFIGURE);
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, setShowConfigureModal]);

  // Strip malformed ?openJobDetails= values (parser returns null)
  useEffect(() => {
    if (
      searchParams.has(MyJobsQueryParamsEnum.OPEN_JOB_DETAILS) &&
      urlParams.openJobDetails == null
    ) {
      clearOpenJobDetailsFromUrl(searchParams, setSearchParams);
    }
  }, [urlParams.openJobDetails, searchParams, setSearchParams]);

  // Deep link from email/todo: route to New only when the id isn't from the current tab.
  useEffect(() => {
    if (urlParams.openJobDetails == null || tabName === MyJobsTabsEnum.NEW) {
      return;
    }

    const listData =
      queryClient.getQueryData<WorkerJobCommonResultsResponse>(listQueryKey);
    const belongsToCurrentTab = listData?.results?.some(
      (result) =>
        result.jobAssignment?.id === urlParams.openJobDetails ||
        result.workerSavedJob?.savedJobId === urlParams.openJobDetails
    );
    if (belongsToCurrentTab) {
      return;
    }

    const query = searchParams.toString();
    navigate(
      `${BASE_PATH}jobs/${currentView}/${MyJobsTabsEnum.NEW}${query ? `?${query}` : ''}`,
      { replace: true }
    );
  }, [
    urlParams.openJobDetails,
    tabName,
    currentView,
    navigate,
    searchParams,
    queryClient,
    listQueryKey,
  ]);

  // Remember query params per tab
  useEffect(() => {
    const queryString = searchParams.toString();
    tabQueryParamsRef.current[currentTab] = queryString;
  }, [searchParams, currentTab]);

  // Hydrate store from URL params on mount (for page refresh scenarios)
  useEffect(() => {
    if (urlParams.searchFilters) {
      // Merge with current filters to ensure all required fields are present
      setSearchFilters({
        companyName: urlParams.searchFilters.companyName ?? '',
        companyId: urlParams.searchFilters.companyId ?? null,
        address: urlParams.searchFilters.address ?? null,
        miles: urlParams.searchFilters.miles ?? null,
      });
    }
    if (urlParams.advancedFilters) {
      setAdvancedFilters({
        ...urlParams.advancedFilters,
        startDate: urlParams.advancedFilters.startDate ?? null,
        endDate: urlParams.advancedFilters.endDate ?? null,
        onGoing: urlParams.advancedFilters.onGoing ?? false,
        jobLocation: urlParams.advancedFilters.jobLocation ?? {
          country: '',
          includeNational: true,
        },
        jobTitles: urlParams.advancedFilters.jobTitles ?? [],
        minHourlyEnabled: urlParams.advancedFilters.minHourlyEnabled ?? false,
        minHourlyRate: urlParams.advancedFilters.minHourlyRate,
        minDailyEnabled: urlParams.advancedFilters.minDailyEnabled ?? false,
        minDailyRate: urlParams.advancedFilters.minDailyRate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.searchFilters, urlParams.advancedFilters]);

  if (!isValidJobScope || !isValidTabName) {
    return <NotFound />;
  }

  const handleChangeView = (val: ViewsTypesEnum) => {
    setCurrentView(val);

    // Temporary redirect to legacy public jobs page until new endpoints are implemented
    if (val === ViewsTypesEnum.ALL_PUBLIC_JOBS) {
      window.location.href = `${import.meta.env.VITE_SERVER_BASE_URL}/publicJobs/index`;
      return;
    }

    navigate(`${BASE_PATH}jobs/${val}/${currentTab}`);
  };

  const handleTabChange = (newTab: string | null) => {
    if (!newTab) return;

    const currentQueryString = searchParams.toString();
    tabQueryParamsRef.current[currentTab] = currentQueryString;

    setCurrentTab(newTab as MyJobsTabsEnum);

    navigate(`${BASE_PATH}jobs/${currentView}/${newTab}`);

    const savedQueryString = tabQueryParamsRef.current[newTab];
    if (savedQueryString) {
      setSearchParams(new URLSearchParams(savedQueryString));
    } else {
      setSearchParams(new URLSearchParams());
      resetAllFilters();
    }
  };

  const handleSearch = (searchData: {
    companyName: string;
    companyId: string | null;
    address: google.maps.places.PlaceResult | null;
    miles: number | null;
  }) => {
    setSearchFilters(searchData);
  };

  const handleAdvancedFiltersApply = (values: AdvancedFiltersFormValues) => {
    setAdvancedFilters(values);
    setShowAdvancedFilters(false);
    if (isMobile) {
      setShowMobileFilters(true);
    }

    // Trigger search with updated advanced filters
    handleMyJobsSearch(searchFilters, values, setSearchParams, {
      itemsPerPage: MY_JOBS_PAGE_SIZE,
      existingSearchParams: searchParams,
    });
  };

  const handleClearSearch = () => {
    resetSearchFilters();
  };

  const handleConfigureModal = () => {
    setShowConfigureModal(true);
  };

  const handleGetInNetworkModal = () => {
    // Show modal for both in-network (to display benefits) and not in-network (to join)
    setShowGetInNetworkModal(true);
  };

  const handleConfigModalClose = () => {
    if (showWelcome) {
      setShowCloseConfirmation(true);
    } else {
      setShowConfigureModal(false);
    }
  };

  const handleCloseAndSaveDefaults = () => {
    if (autoSaveDefaultsRef.current) {
      setIsSavingDefaults(true);
      autoSaveDefaultsRef.current();
    }
  };

  const handleContinueConfiguring = () => {
    setShowCloseConfirmation(false);
    setShowConfigureModal(true);
  };

  const handleViewJobDetails = (recommendation: TransformedJobItem) => {
    if (!recommendation.id) return;

    // Hydrate the by-id cache from the list DTO so the modal opens without a refetch.
    const listData =
      queryClient.getQueryData<WorkerJobCommonResultsResponse>(listQueryKey);
    // Match by job id: row id varies per tab, but job id is stable across lists.
    const dto = listData?.results?.find(
      (result) => result.job?.id === recommendation.jobId
    );
    if (dto) {
      queryClient.setQueryData(
        [QueryKeys.MyJobs.JOB_RECOMMENDATION_BY_ID, recommendation.id],
        dto
      );
    }

    setOpenJobDetailsInUrl(searchParams, setSearchParams, recommendation.id);
  };

  const handleCloseJobDetails = () => {
    clearOpenJobDetailsFromUrl(searchParams, setSearchParams);
  };

  // Wrapper to close job details modal when starting apply flow
  const handleApplyClick = (recommendation: TransformedJobItem) => {
    clearOpenJobDetailsFromUrl(searchParams, setSearchParams);
    applyFlow.startApply(recommendation);
  };

  const showDismissInModal = currentTab === MyJobsTabsEnum.NEW;

  const renderTabsList = () => (
    <Tabs.List
      justify={isMobile ? 'space-between' : undefined}
      w={isMobile ? '100%' : undefined}
    >
      {TABS_CONFIG.map(({ value }) => (
        <Tabs.Tab
          key={value}
          value={value}
          px={{ base: 4, sm: 40 }}
          flex={isMobile ? 1 : undefined}
        >
          {MY_JOBS_TAB_LABELS[value]}
        </Tabs.Tab>
      ))}
    </Tabs.List>
  );

  const renderTabPanels = () => (
    <>
      <Tabs.Panel value={MyJobsTabsEnum.NEW}>
        <JobsList
          onViewDetails={handleViewJobDetails}
          onApply={handleApplyClick}
        />
      </Tabs.Panel>
      <Tabs.Panel value={MyJobsTabsEnum.APPLIED}>
        <JobsList
          onViewDetails={handleViewJobDetails}
          onApply={handleApplyClick}
        />
      </Tabs.Panel>
      <Tabs.Panel value={MyJobsTabsEnum.SAVED}>
        <JobsList
          onViewDetails={handleViewJobDetails}
          onApply={handleApplyClick}
        />
      </Tabs.Panel>
      <Tabs.Panel value={MyJobsTabsEnum.AVAILABILITY}>
        <Availability />
      </Tabs.Panel>
    </>
  );

  return (
    <Stack
      id="my-jobs-content"
      p={{ base: 'xs', sm: 'md' }}
      gap="md"
      flex={1}
      pos="relative"
    >
      {/* Welcome Overlay - First time users */}
      {showWelcome &&
        !showConfigureModal &&
        !showGetInNetworkModal &&
        !showCloseConfirmation && (
          <>
            {/* Dark overlay - below Welcome and modals */}
            <Overlay fixed />
            {/* Welcome Component - above overlay but below modals */}
            <Flex
              pos="fixed"
              top={0}
              left={0}
              w="100vw"
              h="100vh"
              align="center"
              justify="center"
              style={{
                zIndex: getDefaultZIndex('overlay') + 10,
                cursor: 'pointer',
              }}
              onClick={() => setShowCloseConfirmation(true)}
            >
              <Welcome
                style={{ cursor: 'default' }}
                handleConfigure={handleConfigureModal}
                handleGetInNetwork={handleGetInNetworkModal}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            </Flex>
          </>
        )}

      {/* Headers - Desktop vs Mobile */}
      {isMobile ? (
        <HeaderMobile
          value={currentView}
          handleChangeView={handleChangeView}
          handleConfigure={handleConfigureModal}
          handleGetInNetwork={handleGetInNetworkModal}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          onOpenAdvancedFilters={() => setShowAdvancedFilters(true)}
        />
      ) : (
        <>
          <Header
            value={currentView}
            handleChangeView={handleChangeView}
            handleGetInNetwork={handleGetInNetworkModal}
            handleConfigure={handleConfigureModal}
          />
          <FilterBar
            onSearch={handleSearch}
            onClear={handleClearSearch}
            onToggleAdvancedFilters={() =>
              setShowAdvancedFilters(!showAdvancedFilters)
            }
            showAdvancedFilters={showAdvancedFilters}
            withAddress={currentTab === MyJobsTabsEnum.NEW}
          />
        </>
      )}

      {/* Tabs - Shared across Desktop and Mobile */}
      <Flex flex={1} gap="md">
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          keepMounted={false}
          style={{ flex: 1, minWidth: 0 }}
        >
          <Flex align="center" w="100%">
            {renderTabsList()}
          </Flex>
          <Space h="md" />
          {renderTabPanels()}
        </Tabs>

        {/* Advanced Filters - Side panel on desktop, Drawer on mobile */}
        {isMobile ? (
          <Drawer
            opened={showAdvancedFilters}
            onClose={() => setShowAdvancedFilters(false)}
            position="right"
            size="100%"
            withCloseButton={false}
          >
            <AdvancedFilters
              onClose={() => setShowAdvancedFilters(false)}
              onApply={handleAdvancedFiltersApply}
            />
          </Drawer>
        ) : (
          <Transition
            mounted={showAdvancedFilters}
            transition={{
              in: { width: '320px', opacity: 1 },
              out: { width: '0px', opacity: 0 },
              common: { overflow: 'hidden' },
              transitionProperty: 'width, opacity',
            }}
          >
            {(styles) => (
              <div style={{ ...styles, flexShrink: 0 }}>
                <div style={{ width: '320px' }}>
                  <AdvancedFilters
                    onClose={() => setShowAdvancedFilters(false)}
                    onApply={handleAdvancedFiltersApply}
                  />
                </div>
              </div>
            )}
          </Transition>
        )}
      </Flex>

      {/* Hidden JobsConfigForm to populate autoSaveDefaultsRef for first-time users */}
      {showWelcome && !showConfigureModal && (
        <div style={{ display: 'none' }}>
          <JobsConfigForm
            onClose={() => {
              setShowCloseConfirmation(false);
              setIsSavingDefaults(false);
            }}
            handleGetInNetwork={handleGetInNetworkModal}
            onAutoSaveDefaults={(saveFn) => {
              autoSaveDefaultsRef.current = saveFn;
            }}
          />
        </div>
      )}

      <Modal
        opened={showConfigureModal}
        onClose={handleConfigModalClose}
        size="xl"
        title="My Jobs"
      >
        <JobsConfigForm
          onCancel={() => {
            if (showWelcome) {
              setShowCloseConfirmation(true);
            } else {
              setShowConfigureModal(false);
            }
          }}
          onClose={() => setShowConfigureModal(false)}
          handleGetInNetwork={handleGetInNetworkModal}
          onAutoSaveDefaults={(saveFn) => {
            autoSaveDefaultsRef.current = saveFn;
          }}
        />
      </Modal>

      {/* Confirmation Modal for first-time users */}
      <Modal
        opened={showCloseConfirmation}
        onClose={handleContinueConfiguring}
        title="Save Default Preferences?"
        centered
      >
        <Stack gap="md">
          <Text>
            If you close now, we&apos;ll save the default preferences for you.
            You can always change them later by clicking the Configure button.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={handleContinueConfiguring}
              disabled={isSavingDefaults}
            >
              Continue Configuring
            </Button>
            <Button
              onClick={handleCloseAndSaveDefaults}
              loading={isSavingDefaults}
            >
              Close & Save Defaults
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={showGetInNetworkModal}
        onClose={() => setShowGetInNetworkModal(false)}
        size={900}
        withCloseButton={false}
        radius="md"
        centered
        styles={{
          body: { padding: 0 },
          content: { overflow: 'hidden', padding: 0 },
        }}
      >
        <GetInNetwork
          onJoin={() => setShowGetInNetworkModal(false)}
          onClose={() => setShowGetInNetworkModal(false)}
        />
      </Modal>

      {/* Job Details Modal */}
      <JobDetailsModal
        opened={showJobDetailsModal}
        onClose={handleCloseJobDetails}
        jobRecommendationId={urlParams.openJobDetails ?? null}
        onDismiss={
          showDismissInModal ? recommendationActions.handleDismiss : undefined
        }
        onBookmark={recommendationActions.handleBookmark}
        onShare={recommendationActions.handleShare}
        onMessage={recommendationActions.handleMessage}
        onApply={handleApplyClick}
        mutation={recommendationActions.mutationState}
      />

      {/* Application Flow Modals */}
      <JobFarAwayModal {...applyFlow.modals.distance} />
      <ProfileIncompleteModal {...applyFlow.modals.profile} />
      <JobQuestionsModal {...applyFlow.modals.questions} />
    </Stack>
  );
}

export default MyJobs;
